import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    ChevronRight,
    ChevronLeft,
    Users,
    Building2,
    BookOpen,
    Search,
    UserPlus,
    BarChart3,
    Map,
    Shield,
    LogIn,
    FileText,
    Upload,
    Handshake,
    Settings,
    CheckCircle,
    ArrowRight,
    GraduationCap
} from 'lucide-react';

const steps = [
    {
        id: 'welcome',
        title: 'Welcome to HAAPE Hub',
        icon: GraduationCap,
        color: 'bg-teal-500',
        content: (
            <>
                <p className="text-muted-foreground mb-4">
                    The HAAPE Hub is the online platform for <strong>Helping Adults with Autism Perform and Excel</strong>. 
                    It connects employers, training providers, support organizations, and community partners through our 
                    Workforce Ecosystem.
                </p>
                <p className="text-muted-foreground mb-4">
                    This guide will walk you through everything you need to know to get started as a member.
                </p>
                <div className="bg-teal-50 dark:bg-teal-950/30 rounded-lg p-4 border border-teal-200 dark:border-teal-800">
                    <p className="text-sm font-medium text-teal-800 dark:text-teal-200">
                        HAAPE's Mission: To create autism workforce ecosystems focused on training and education that empower 
                        autistic individuals to obtain meaningful and sustainable careers.
                    </p>
                </div>
            </>
        ),
    },
    {
        id: 'account',
        title: 'Creating Your Account',
        icon: LogIn,
        color: 'bg-blue-500',
        content: (
            <>
                <p className="text-muted-foreground mb-4">
                    To access member features, you'll need to create an account. You have two options:
                </p>
                <div className="space-y-3 mb-4">
                    <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center shrink-0 mt-0.5">
                            <span className="text-sm font-bold text-blue-600">1</span>
                        </div>
                        <div>
                            <p className="font-medium">Google Sign-In</p>
                            <p className="text-sm text-muted-foreground">Click "Continue with Google" for the fastest setup</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                        <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center shrink-0 mt-0.5">
                            <span className="text-sm font-bold text-blue-600">2</span>
                        </div>
                        <div>
                            <p className="font-medium">Email & Password</p>
                            <p className="text-sm text-muted-foreground">Create an account with your name, email, and a password (min 8 characters)</p>
                        </div>
                    </div>
                </div>
                <div className="bg-amber-50 dark:bg-amber-950/30 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                        <strong>Note:</strong> After creating your account, your membership will be pending approval. 
                        Our team reviews all new accounts within about 7 business days to maintain a trusted community.
                    </p>
                </div>
            </>
        ),
    },
    {
        id: 'directory',
        title: 'The Public Directory',
        icon: Building2,
        color: 'bg-green-500',
        content: (
            <>
                <p className="text-muted-foreground mb-4">
                    The Public Directory contains all organizations in our network. Here's how to use it:
                </p>
                <div className="space-y-3 mb-4">
                    <div className="flex items-start gap-3">
                        <Search className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                        <div>
                            <p className="font-medium">Search & Filter</p>
                            <p className="text-sm text-muted-foreground">Use the search bar to find organizations by name, or filter by state and type using the sidebar</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3">
                        <Building2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                        <div>
                            <p className="font-medium">Explore by Category</p>
                            <p className="text-sm text-muted-foreground">Click category buttons at the top (Employers, Training, Higher Ed, etc.) to quickly filter results</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3">
                        <FileText className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                        <div>
                            <p className="font-medium">Export Results</p>
                            <p className="text-sm text-muted-foreground">Click the Export button to download your search results as an Excel spreadsheet or PDF</p>
                        </div>
                    </div>
                </div>
                <Link to="/directory">
                    <Button variant="outline" size="sm" className="gap-2">
                        <Building2 className="h-4 w-4" />
                        Go to Public Directory
                        <ArrowRight className="h-4 w-4" />
                    </Button>
                </Link>
            </>
        ),
    },
    {
        id: 'members',
        title: 'Member Directory',
        icon: Users,
        color: 'bg-purple-500',
        content: (
            <>
                <p className="text-muted-foreground mb-4">
                    The Member Directory shows verified HAAPE members — organizations that have been vetted and approved 
                    to be part of our trusted network. You can browse by organization type to find the right partners.
                </p>
                <p className="text-muted-foreground mb-4">
                    Organizations with the <Badge className="bg-teal-100 text-teal-800 text-xs mx-1"><Shield className="h-3 w-3 inline mr-1" />HAAPE Member</Badge> badge 
                    have been claimed by a verified member and contribute to our collective impact metrics.
                </p>
                <Link to="/members">
                    <Button variant="outline" size="sm" className="gap-2">
                        <Users className="h-4 w-4" />
                        Go to Member Directory
                        <ArrowRight className="h-4 w-4" />
                    </Button>
                </Link>
            </>
        ),
    },
    {
        id: 'contribute',
        title: 'Contributing to the Hub',
        icon: Upload,
        color: 'bg-cyan-500',
        content: (
            <>
                <p className="text-muted-foreground mb-4">
                    As a member, you can contribute to the HAAPE Hub in several ways. Look for the <strong>"Contribute"</strong> menu in the navigation bar:
                </p>
                <div className="space-y-3 mb-4">
                    <div className="flex items-start gap-3 p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg border border-purple-100 dark:border-purple-900">
                        <Handshake className="h-5 w-5 text-purple-600 shrink-0 mt-0.5" />
                        <div>
                            <p className="font-medium">Nominate a Member</p>
                            <p className="text-sm text-muted-foreground">Recommend an organization for our trusted member network. Nominations are reviewed by admins.</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-100 dark:border-blue-900">
                        <Building2 className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                        <div>
                            <p className="font-medium">Add to Public Directory</p>
                            <p className="text-sm text-muted-foreground">Add organizations directly to our searchable directory. These go live immediately.</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-teal-50 dark:bg-teal-950/30 rounded-lg border border-teal-100 dark:border-teal-900">
                        <BookOpen className="h-5 w-5 text-teal-600 shrink-0 mt-0.5" />
                        <div>
                            <p className="font-medium">Add a Resource</p>
                            <p className="text-sm text-muted-foreground">Share articles, guides, or documents. Upload a PDF or add a link.</p>
                        </div>
                    </div>
                </div>
            </>
        ),
    },
    {
        id: 'resources',
        title: 'Resource Library',
        icon: BookOpen,
        color: 'bg-amber-500',
        content: (
            <>
                <p className="text-muted-foreground mb-4">
                    The Resource Library is our curated collection of articles, guides, toolkits, and documents 
                    relevant to autism employment and support. Browse by category or search by keyword.
                </p>
                <p className="text-muted-foreground mb-4">
                    Members can contribute their own resources through the "Contribute" menu — share knowledge that 
                    could help others in our community.
                </p>
                <Link to="/resources">
                    <Button variant="outline" size="sm" className="gap-2">
                        <BookOpen className="h-4 w-4" />
                        Browse Resource Library
                        <ArrowRight className="h-4 w-4" />
                    </Button>
                </Link>
            </>
        ),
    },
    {
        id: 'impact',
        title: 'Impact Dashboard',
        icon: BarChart3,
        color: 'bg-emerald-500',
        content: (
            <>
                <p className="text-muted-foreground mb-4">
                    The Impact Dashboard shows the collective impact of our HAAPE Member organizations. Metrics include:
                </p>
                <div className="grid grid-cols-2 gap-2 mb-4">
                    {['People Trained', 'People Employed', 'Completion Rate', 'Placement Rate', 'Employers Engaged', 'Avg Starting Salary'].map(m => (
                        <div key={m} className="flex items-center gap-2 text-sm">
                            <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                            <span className="text-muted-foreground">{m}</span>
                        </div>
                    ))}
                </div>
                <p className="text-muted-foreground mb-4 text-sm">
                    If you manage a claimed organization, you can submit your annual impact metrics through the 
                    "Submit Impact Metrics" option in the Contribute menu.
                </p>
                <Link to="/impact">
                    <Button variant="outline" size="sm" className="gap-2">
                        <BarChart3 className="h-4 w-4" />
                        View Impact Dashboard
                        <ArrowRight className="h-4 w-4" />
                    </Button>
                </Link>
            </>
        ),
    },
    {
        id: 'admin',
        title: 'For Administrators',
        icon: Settings,
        color: 'bg-red-500',
        content: (
            <>
                <p className="text-muted-foreground mb-4">
                    Administrators have access to the Admin Dashboard where you can:
                </p>
                <div className="space-y-2 mb-4">
                    {[
                        'Manage Members — approve accounts, assign roles, create new members',
                        'Review Submissions — member nominations, directory listings, and resources',
                        'Manage Public Directory — edit, add, or remove organizations',
                        'Resource Library — add and manage CMS content',
                        'Export & Import — bulk data management',
                        'Manage Chapters — create chapters and assign Chapter Directors',
                        'Send Notifications — email admins about items needing review',
                    ].map((item, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm">
                            <CheckCircle className="h-3.5 w-3.5 text-red-500 shrink-0 mt-0.5" />
                            <span className="text-muted-foreground">{item}</span>
                        </div>
                    ))}
                </div>
                <Link to="/admin">
                    <Button variant="outline" size="sm" className="gap-2">
                        <Settings className="h-4 w-4" />
                        Go to Admin Dashboard
                        <ArrowRight className="h-4 w-4" />
                    </Button>
                </Link>
            </>
        ),
    },
];

const GettingStartedPage = () => {
    const { isAuthenticated } = useAuth();
    const [currentStep, setCurrentStep] = useState(0);

    const step = steps[currentStep];
    const StepIcon = step.icon;
    const progress = ((currentStep + 1) / steps.length) * 100;

    return (
        <div className="min-h-screen bg-background" data-testid="getting-started-page">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
                {/* Header */}
                <div className="text-center mb-8">
                    <Badge variant="secondary" className="mb-3">Getting Started Guide</Badge>
                    <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2">Learn the HAAPE Hub</h1>
                    <p className="text-muted-foreground">A step-by-step walkthrough of everything you can do</p>
                </div>

                {/* Progress */}
                <div className="mb-8">
                    <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                        <span>Step {currentStep + 1} of {steps.length}</span>
                        <span>{Math.round(progress)}% complete</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
                    </div>
                </div>

                {/* Step Navigation Dots */}
                <div className="flex items-center justify-center gap-2 mb-8 flex-wrap">
                    {steps.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <button
                                key={s.id}
                                onClick={() => setCurrentStep(i)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                                    i === currentStep
                                        ? 'bg-primary text-primary-foreground shadow-sm'
                                        : i < currentStep
                                        ? 'bg-primary/10 text-primary'
                                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                                }`}
                                data-testid={`step-${s.id}`}
                            >
                                <Icon className="h-3 w-3" />
                                <span className="hidden sm:inline">{s.title.split(' ').slice(0, 2).join(' ')}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Step Content */}
                <Card className="mb-8">
                    <CardContent className="p-6 sm:p-8">
                        <div className="flex items-center gap-3 mb-6">
                            <div className={`w-12 h-12 rounded-xl ${step.color} flex items-center justify-center`}>
                                <StepIcon className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground font-medium">Step {currentStep + 1}</p>
                                <h2 className="font-heading text-xl font-bold">{step.title}</h2>
                            </div>
                        </div>
                        {step.content}
                    </CardContent>
                </Card>

                {/* Navigation Buttons */}
                <div className="flex items-center justify-between">
                    <Button
                        variant="outline"
                        onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                        disabled={currentStep === 0}
                        className="gap-2"
                    >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                    </Button>

                    {currentStep < steps.length - 1 ? (
                        <Button onClick={() => setCurrentStep(currentStep + 1)} className="gap-2">
                            Next
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    ) : (
                        <Link to={isAuthenticated ? '/welcome' : '/'}>
                            <Button className="gap-2">
                                Get Started
                                <ArrowRight className="h-4 w-4" />
                            </Button>
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GettingStartedPage;
